import React, { useState, useEffect } from 'react';
import api from '../../utils/axiosConfig';
import { useNavigate } from 'react-router-dom';
import PageLoadingArea from '../../components/PageLoader/PageLoadingArea';
import usePageLoading from '../../hooks/usePageLoading';
import '../../styles/ListPage.css';

function VendorTypeList() {
    const navigate = useNavigate();
    const { isLoading, withLoading } = usePageLoading(true);
    const [vendorTypes, setVendorTypes] = useState([]);
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredTypes, setFilteredTypes] = useState([]);

    useEffect(() => {
        withLoading(async () => {
            const res = await api.get('/vendor-types');
            setVendorTypes(res.data);
            setFilteredTypes(res.data);
        }).catch((err) => console.error('Error fetching vendor types:', err));
    }, [withLoading]);

    useEffect(() => {
        const term = searchTerm.toLowerCase();
        const results = vendorTypes.filter(vendorType =>
            (vendorType.type || '').toLowerCase().includes(term)
        );
        setFilteredTypes(results);
    }, [searchTerm, vendorTypes]);

    const handleCheckboxChange = (typeId) => {
        setSelectedTypes(prev =>
            prev.includes(typeId) ? prev.filter(id => id !== typeId) : [...prev, typeId]
        );
    };

    const handleSelectAll = (e) => {
        setSelectedTypes(e.target.checked ? filteredTypes.map(type => type.id) : []);
    };

    const handleDeleteSelected = () => {
        if (!window.confirm('Are you sure you want to delete the selected vendor types?')) return;
        Promise.all(selectedTypes.map(typeId => api.delete(`/vendor-types/${typeId}`)))
            .then(() => {
                setVendorTypes(prev => prev.filter(type => !selectedTypes.includes(type.id)));
                setSelectedTypes([]);
            })
            .catch((err) => {
                if (err.response && err.response.status === 400) {
                    alert('Cannot delete vendor types with associated vendors');
                } else {
                    alert('Error deleting vendor types');
                }
            });
    };

    const allSelected = filteredTypes.length > 0 && selectedTypes.length === filteredTypes.length;

    return (
        <PageLoadingArea isLoading={isLoading}>
        <div className="list-page">
            <div className="list-page-header">
                <div className="list-page-title">
                    <h1>Vendor Types</h1>
                    <p className="list-page-subtitle">
                        {filteredTypes.length} type{filteredTypes.length === 1 ? '' : 's'}
                    </p>
                </div>
                <div className="list-header-actions">
                    <div className="list-search-wrap">
                        <input
                            type="text"
                            placeholder="Search vendor type"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="list-search-input"
                        />
                    </div>
                    <div className="list-button-group">
                        <button
                            type="button"
                            onClick={() => navigate('/admin/vendors')}
                            className="list-secondary-button"
                        >
                            Back to vendors
                        </button>
                        <button type="button" onClick={() => navigate('/createVendorType')} className="list-add-button">
                            Add New Type
                        </button>
                        {selectedTypes.length > 0 && (
                            <button type="button" onClick={handleDeleteSelected} className="list-delete-button">
                                Delete ({selectedTypes.length})
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="list-table-card">
                <div className="list-table-scroll">
                    <table className="list-table">
                        <thead>
                            <tr>
                                <th className="col-check">
                                    <input type="checkbox" checked={allSelected} onChange={handleSelectAll} aria-label="Select all" />
                                </th>
                                <th>Sr No</th>
                                <th>Type</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTypes.length === 0 ? (
                                <tr className="list-empty-row">
                                    <td colSpan="3">
                                        <div className="list-empty-state">
                                            <h3>No vendor types found</h3>
                                            <p>{searchTerm ? 'Try a different search term.' : 'Add your first vendor type to get started.'}</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredTypes.map((vendorType, index) => (
                                <tr
                                    key={vendorType.id}
                                    className={`list-row${selectedTypes.includes(vendorType.id) ? ' is-selected' : ''}`}
                                    onClick={(e) => {
                                        if (e.target.type !== 'checkbox') navigate(`/createVendorType/${vendorType.id}`);
                                    }}
                                >
                                    <td className="col-check" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={selectedTypes.includes(vendorType.id)}
                                            onChange={() => handleCheckboxChange(vendorType.id)}
                                        />
                                    </td>
                                    <td>{index + 1}</td>
                                    <td><span className="list-primary-text">{vendorType.type}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        </PageLoadingArea>
    );
}

export default VendorTypeList;
